/**
 * InterestsCard — single "Interests" card combining Journey Stage, Topics, and
 * Archetypes (was three separate cards). One "Show on profile" toggle in the
 * header drives the visibility of all three sub-sections together.
 *
 * @route /profile/edit
 * @parent CommunityProfileSettingsPage
 */

import React, { Suspense, useCallback, useMemo } from 'react'
import { H2 } from '../../../../../Heading/index'
import Button, { TYPES as BUTTON_TYPES } from '../../../../../Button/index'
import EyeHideSolid from '../../../../../Icon/icons/EyeHideSolid/index'
import ToggleSwitch from '../../../../shared/ToggleSwitch/index'
import CommunityProfileTopicTag from '../../CommunityProfileTopicTag/index'
import { useTranslatedText } from '../../../../../StaticText/StaticTextContext'
import { MAX_USER_TAGS } from '../../../../../../services/community/index'
import { ARCHETYPES, JOURNEY_OPTIONS, MAX_ARCHETYPES } from '../../../../../../services/community/identity/profile/constants'
import getArchetypeIcon from '../../../../../../services/community/identity/profile/archetype-icons'

const CLS = 'community-profile-settings'

export interface TopicOption {
  id: string | number
  label: string
  tag?: string
}

export interface InterestsCardProps {
  availableTopics: TopicOption[]
  selectedTagIds: (string | number)[]
  onTagToggle: (updater: (prev: (string | number)[]) => (string | number)[]) => void
  selectedArchetypes: string[]
  onArchetypeToggle: (key: string) => void
  journeyStage: string | null
  onJourneyChange: (key: string) => void
  interestsVisible: boolean
  onInterestsVisibilityChange: (visible: boolean) => void
  profileVisibility?: string
}

/**
 * Inner component for a single journey radio entry — encapsulates the
 * `useTranslatedText` hook call so it can be invoked once per entry inside
 * the iteration without violating the rules of hooks.
 */
interface JourneyEntry {
  key: string
  labelKey: string
}

function InterestsCard ({
  availableTopics,
  selectedTagIds,
  onTagToggle,
  selectedArchetypes,
  onArchetypeToggle,
  journeyStage,
  onJourneyChange,
  interestsVisible,
  onInterestsVisibilityChange,
  profileVisibility = 'public',
}: InterestsCardProps) {
  const isStealthMode = profileVisibility === 'private'
  const atLimit = selectedTagIds.length >= MAX_USER_TAGS

  const interestsTitleText = useTranslatedText({ keys: ['interestsTitle'] })
  const privateProfileText = useTranslatedText({ keys: ['privateProfile'] })
  const showOnProfileText = useTranslatedText({ keys: ['showOnProfile'] })
  const interestsIntroDescriptionText = useTranslatedText({ keys: ['interestsIntroDescription'] })
  const journeyStageHeadingText = useTranslatedText({ keys: ['journeyStageHeading'] })
  const journeyDescriptionText = useTranslatedText({ keys: ['journeyDescription'] })
  const topicsSubheadingText = useTranslatedText({ keys: ['topicsSubheading'] })
  const topicsDescriptionText = useTranslatedText({ keys: ['topicsDescription'] })
  const topicsMaxReachedText = useTranslatedText({ keys: ['topicsMaxReached'] })
  const archetypesSubheadingText = useTranslatedText({ keys: ['archetypesSubheading'] })
  const archetypesDescriptionText = useTranslatedText({ keys: ['archetypesDescription'] })

  // Memoized map of lazy archetype icon components
  const archetypeIconComponents = useMemo(() => {
    const map: Record<string, ReturnType<typeof getArchetypeIcon>> = {}
    ARCHETYPES.forEach((arch) => {
      map[arch.key] = getArchetypeIcon(arch.key)
    })
    return map
  }, [])

  const handleToggle = useCallback((tagId: string | number) => {
    onTagToggle((prev) => {
      if (prev.includes(tagId)) return prev.filter((id) => id !== tagId)
      if (prev.length >= MAX_USER_TAGS) return prev
      return [...prev, tagId]
    })
  }, [onTagToggle])

  const journeyEntries = useMemo<JourneyEntry[]>(() => [
    ...JOURNEY_OPTIONS.map((opt) => ({
      key: opt.key,
      labelKey: `journey${opt.key.charAt(0).toUpperCase()}${opt.key.slice(1)}`,
    })),
    {
      key: 'not-sure',
      labelKey: 'journeyNotSure',
    },
  ], [])

  return (
    <div className={`${CLS}__card ${CLS}__card--bordered ${CLS}__card--interests`}>
      {/* Header: title + Show on profile toggle + intro description */}
      <div className={`${CLS}__interests-section`}>
        <div className={`${CLS}__section-heading`}>
          <H2 className={`${CLS}__card-title`}>
            {interestsTitleText}
          </H2>
          {isStealthMode ? (
            <span className={`${CLS}__badge ${CLS}__badge--private`}>
              <EyeHideSolid />
              {privateProfileText}
            </span>
          ) : (
            <div className={`${CLS}__toggle-inline-row`}>
              <span className={`${CLS}__toggle-inline-label`}>
                {showOnProfileText}
              </span>
              <ToggleSwitch
                checked={interestsVisible}
                onChange={onInterestsVisibilityChange}
                label={interestsTitleText}
                showEyeIcon
              />
            </div>
          )}
        </div>
        <p className={`${CLS}__card-subtitle ${CLS}__card-subtitle--lg`}>
          {interestsIntroDescriptionText}
        </p>
      </div>

      {/* Journey Stage */}
      <div className={`${CLS}__interests-subsection ${CLS}__interests-subsection--journey`}>
        <div className={`${CLS}__interests-section`}>
          <H2 className={`${CLS}__interests-subheading`}>
            {journeyStageHeadingText}
          </H2>
          <p className={`${CLS}__card-subtitle`}>
            {journeyDescriptionText}
          </p>
        </div>
        <div
          className={`${CLS}__journey-options`}
          role="radiogroup"
          aria-label={journeyStageHeadingText}
        >
          {journeyEntries.map((entry) => (
            <JourneyRadio
              key={entry.key}
              entryKey={entry.key}
              labelKey={entry.labelKey}
              selected={journeyStage === entry.key}
              onChange={onJourneyChange}
            />
          ))}
        </div>
      </div>

      <hr className={`${CLS}__interests-divider`} />

      {/* Topics */}
      <div className={`${CLS}__interests-subsection`}>
        <div className={`${CLS}__interests-section`}>
          <H2 className={`${CLS}__interests-subheading`}>
            {topicsSubheadingText}
          </H2>
          <p className={`${CLS}__card-subtitle`}>
            {topicsDescriptionText}
          </p>
        </div>
        <div className={`${CLS}__topics-grid`}>
          {availableTopics.map((opt) => {
            const isSelected = selectedTagIds.includes(opt.id)
            return (
              <CommunityProfileTopicTag
                key={opt.id}
                label={opt.label}
                selected={isSelected}
                disabled={!isSelected && atLimit}
                onClick={() => handleToggle(opt.id)}
              />
            )
          })}
        </div>
        <span
          className={`${CLS}__topics-max-hint${
            !atLimit ? ` ${CLS}__topics-max-hint--hidden` : ''
          }`}
        >
          {topicsMaxReachedText}
        </span>
      </div>

      <hr className={`${CLS}__interests-divider`} />

      {/* Archetypes */}
      <div className={`${CLS}__interests-subsection`}>
        <div className={`${CLS}__interests-section`}>
          <H2 className={`${CLS}__interests-subheading`}>
            {archetypesSubheadingText}
          </H2>
          <p className={`${CLS}__card-subtitle`}>
            {archetypesDescriptionText}
          </p>
        </div>
        <div className={`${CLS}__archetype-grid`}>
          {ARCHETYPES.map((arch) => (
            <ArchetypeCard
              key={arch.key}
              archetypeKey={arch.key}
              isSelected={selectedArchetypes.includes(arch.key)}
              isDisabled={
                !selectedArchetypes.includes(arch.key)
                && selectedArchetypes.length >= MAX_ARCHETYPES
              }
              IconComponent={archetypeIconComponents[arch.key]}
              onToggle={onArchetypeToggle}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

/**
 * Individual archetype selection card. Extracted so each archetype's label
 * and description can be resolved via `useTranslatedText` without calling
 * hooks inside a `.map(...)`. Lookup keys are derived from the stable
 * archetype identifier (e.g. `archetype_seeker` / `archetype_seekerDesc`)
 * and live under the `communityProfileSettingsPage` slice.
 */
interface ArchetypeCardProps {
  archetypeKey: string
  isSelected: boolean
  isDisabled: boolean
  IconComponent: ReturnType<typeof getArchetypeIcon> | undefined
  onToggle: (key: string) => void
}

function ArchetypeCard ({
  archetypeKey,
  isSelected,
  isDisabled,
  IconComponent,
  onToggle,
}: ArchetypeCardProps) {
  const label = useTranslatedText({ keys: [`archetype_${archetypeKey}`] })
  const desc = useTranslatedText({ keys: [`archetype_${archetypeKey}Desc`] })
  return (
    <Button
      type={BUTTON_TYPES.GHOST}
      className={`${CLS}__archetype-card${
        isSelected ? ` ${CLS}__archetype-card--selected` : ''
      }${isDisabled ? ` ${CLS}__archetype-card--disabled` : ''}`}
      onClick={() => onToggle(archetypeKey)}
      disabled={isDisabled}
    >
      <div className={`${CLS}__archetype-heading`}>
        <span className={`${CLS}__archetype-label`}>
          {label}
        </span>
        <div className={`${CLS}__archetype-icon-placeholder`}>
          {IconComponent ? (
            <Suspense fallback={null}>
              <IconComponent size={100} />
            </Suspense>
          ) : null}
        </div>
      </div>
      <span className={`${CLS}__archetype-desc`}>
        {desc}
      </span>
    </Button>
  )
}

/**
 * Individual journey radio row. Extracted so `useTranslatedText` can resolve
 * each journey option's label without violating the rules of hooks in a
 * map() loop.
 */
interface JourneyRadioProps {
  entryKey: string
  labelKey: string
  selected: boolean
  onChange: (key: string) => void
}

function JourneyRadio ({
  entryKey, labelKey, selected, onChange,
}: JourneyRadioProps) {
  const label = useTranslatedText({ keys: [labelKey] })
  const separatorIdx = label.indexOf(' - ')
  const title = separatorIdx >= 0 ? label.slice(0, separatorIdx) : label
  const rest = separatorIdx >= 0 ? label.slice(separatorIdx) : ''
  return (
    <label className={`${CLS}__journey-radio`} htmlFor={`journey-stage-${entryKey}`}>
      <input
        id={`journey-stage-${entryKey}`}
        type="radio"
        name="journey-stage"
        value={entryKey}
        checked={selected}
        onChange={() => onChange(entryKey)}
        className={`${CLS}__journey-radio-input`}
      />
      <span className={`${CLS}__journey-radio-circle`} />
      <span className={`${CLS}__journey-radio-label`}>
        <span className={`${CLS}__journey-radio-label-title`}>{title}</span>
        {rest}
      </span>
    </label>
  )
}

export default React.memo(InterestsCard)
